import { ref, onMounted, onUnmounted } from 'vue';

/**
 * Composable for detecting Bootstrap breakpoints using matchMedia
 * Uses the same breakpoint values as Bootstrap 5
 *
 * @param breakpoint - Bootstrap breakpoint name (sm, md, lg, xl, xxl)
 * @returns Reactive ref that is true when viewport is below the breakpoint
 *
 * @example
 * ```ts
 * const isBelowLg = useBootstrapBreakpoint('lg');
 * // isBelowLg.value is true when viewport < 992px
 * ```
 */
export function useBootstrapBreakpoint(breakpoint: 'sm' | 'md' | 'lg' | 'xl' | 'xxl') {
  // Bootstrap 5 breakpoint values (max-width)
  // These match the official Bootstrap 5 breakpoints exactly
  const breakpoints = {
    sm: '(max-width: 575.98px)',   // < 576px
    md: '(max-width: 767.98px)',   // < 768px
    lg: '(max-width: 991.98px)',   // < 992px
    xl: '(max-width: 1199.98px)',  // < 1200px
    xxl: '(max-width: 1399.98px)', // < 1400px
  };

  const isBelowBreakpoint = ref(false);
  let mediaQuery: MediaQueryList | null = null;

  const updateMatch = (e: MediaQueryListEvent | MediaQueryList) => {
    isBelowBreakpoint.value = e.matches;
  };

  onMounted(() => {
    mediaQuery = window.matchMedia(breakpoints[breakpoint]);
    isBelowBreakpoint.value = mediaQuery.matches;
    mediaQuery.addEventListener('change', updateMatch);
  });

  onUnmounted(() => {
    if (mediaQuery) {
      mediaQuery.removeEventListener('change', updateMatch);
    }
  });

  return isBelowBreakpoint;
}